import type { RecorderController } from './RecorderController';
import { holdRecorderWriterLease } from './recorderLease';
import { BrowserRecorderSettlementLock, type RecorderSettlementLock } from './recorderSettlementLock';
import { BrowserRecorderUploadSignal, type RecorderUploadSignal } from './recorderUploadSignal';
import { requestWentUnanswered } from './requestAnswer';
import type {
  LocalRecordingSession,
  LocalRecordingUpload,
  RecorderUploadStore,
  RecordingChunk,
  RecordingServerSession,
} from './types';

const CLIENT_UPLOAD_PART_BYTES = 5 * 1024 * 1024;
/**
 * The smallest part a slow line is cut down to (TOADO-991, review round 1).
 * The server keeps at most 1,024 parts a recording, and at this size that is
 * a gigabyte, which a line this slow could not send in a day anyway. Parts
 * grow back towards CLIENT_UPLOAD_PART_BYTES as soon as they land quickly.
 */
const MIN_CLIENT_UPLOAD_PART_BYTES = 1024 * 1024;
const MAX_AUTOMATIC_ATTEMPTS = 5;
/**
 * The longest one part of the master upload may take (TOADO-991). A request
 * starved of bandwidth can make no progress and never fail, which left the
 * walkthrough "uploading" for good with nothing retrying it. Ten minutes is
 * 5 MB at about 70 kbit/s; past it the part is dropped, the attempt fails, the
 * next part is half the size, and the next attempt starts by itself from the
 * last part the server acknowledged. At the smallest part the deadline is
 * about 14 kbit/s, so only a line that has all but stopped is given up on.
 */
export const UPLOAD_PART_DEADLINE_MS = 10 * 60 * 1_000;
/** The longest any other request of the upload may take: they carry no video (TOADO-991, review round 6). */
export const UPLOAD_REQUEST_DEADLINE_MS = 2 * 60 * 1_000;
/**
 * A part is given this many times as long as the parts before it took, and no
 * less than STALL_FLOOR_MS (TOADO-991, 28 Oct). Jason's walkthrough landed
 * 5 MB parts in two seconds and then had one part freeze outright, twice, on a
 * fast line: something on the machine held the request. Ten minutes was right
 * for a slow line and far too long for that. Measured from this upload's own
 * parts, so a slow line keeps the time it needs.
 */
const STALL_FACTOR = 20;
export const STALL_FLOOR_MS = 60_000;
/** How many measurements a deadline follows: the last few, so newer parts retire old ones (review round 1). */
const RECENT_RATES = 3;
/** How old a stored measurement may be and still be used: an upload resumed later may be on another line (review round 1). */
export const RATE_FRESH_MS = 30 * 60 * 1_000;
/** How far ahead of the clock a measurement's time may be, for a clock set back a little (review round 2). */
const RATE_CLOCK_SLACK_MS = 60_000;
/**
 * Each part that times out under a measured deadline doubles the next one's,
 * up to the fixed deadline, until a part lands (review round 2): one, two,
 * four, eight minutes. A timeout says nothing of the line's speed, since a
 * request held on a fast line times out as surely as one on a slow line, so it
 * is not measured: counted as speed (round 1's fix), one held request gave the
 * next its full ten minutes, the stall this exists to end. Doubling lets a line
 * that slowed after fast parts land within a few attempts, and drops a held
 * request on a fast line in one minute, then two.
 *
 * At the most, a thousand times the measured deadline, which is the fixed
 * deadline whatever the floor: what the last automatic attempt gets, and the
 * attempt after the smallest part has timed out again, which halving cannot
 * help (review round 3). So a line the fixed deadline served is never parked
 * without being given it, though a request held on a fast line is then held
 * the full deadline on that attempt. Only the fifth automatic attempt is the
 * last: a person's retry after parking, at five or more, is measured like any
 * other, so it does not hold a request on a fast line for the full deadline
 * every time it is pressed (review round 4).
 */
const MAX_STRETCH = 10;

/**
 * How long a part may take (TOADO-991): a size the server is holding gets its
 * own allowance (`forcedDeadline`); else, once this line has been measured,
 * STALL_FACTOR times the slowest recent part's time per byte for these bytes,
 * no less than the floor, doubled for each part that has timed out since one
 * last landed (`stretch`), and no more than the fixed deadline; else the
 * fixed deadline. Times are whole requests, the server's handling and the
 * answer included, which only makes a deadline longer.
 */
export function partDeadline(input: {
  bytes: number;
  rates: ReadonlyArray<{ msPerByte: number }>;
  stretch: number;
  deadlineMs: number;
  floorMs: number;
  forcedDeadline: number | null;
}): number {
  if (input.forcedDeadline !== null) return input.forcedDeadline;
  if (input.rates.length === 0) return input.deadlineMs;
  const slowest = Math.max(...input.rates.map((rate) => rate.msPerByte));
  const measured = Math.max(input.floorMs, Math.ceil(input.bytes * slowest * STALL_FACTOR));
  return Math.min(input.deadlineMs, measured * 2 ** Math.min(MAX_STRETCH, Math.max(0, input.stretch)));
}

/** The stored measurements a run can use: well formed, recent, the last few (review round 1). */
export function freshRates(stored: unknown, nowMs: number): Array<{ at: number; msPerByte: number }> {
  if (!Array.isArray(stored)) return [];
  return stored
    .filter((rate): rate is { at: number; msPerByte: number } => typeof rate === 'object' && rate !== null
      && validRate((rate as { msPerByte?: unknown }).msPerByte)
      && typeof (rate as { at?: unknown }).at === 'number' && Number.isFinite((rate as { at: number }).at)
      && nowMs - (rate as { at: number }).at <= RATE_FRESH_MS
      && (rate as { at: number }).at <= nowMs + RATE_CLOCK_SLACK_MS)
    .slice(-RECENT_RATES);
}
/**
 * How long after a failed attempt the next one starts by itself (review round
 * 1): nothing else asks in a single window, since the cross-tab signal reaches
 * only other windows and `online` never fires on a line that is merely slow.
 * Doubling from fifteen seconds, at most five minutes.
 */
export function automaticRetryDelayMs(attempts: number): number {
  return Math.min(5 * 60 * 1_000, 15_000 * 2 ** Math.max(0, attempts - 1));
}
const DEFAULT_PROCESSING_POLL_MS = 2_000;
const MAX_PROCESSING_POLL_MS = 30_000;
const MAX_PROCESSING_WAIT_MS = 45 * 60 * 1_000;
/** The shortest deadline a processing check is given as the wait runs out (TOADO-995, review round 1). */
const LAST_PROCESSING_CHECK_MS = 5_000;

/**
 * Nothing automatic should pick this upload up again.
 *
 * Two ways to get here, and they are not the same. The retries are spent, or
 * the server has given an answer that resending cannot change. Both are read
 * from what they actually are rather than one being written as the other:
 * forging `attempts` to park a refusal spends the whole retry budget, so the
 * first dropped connection AFTER the person fixes the refusal parks the
 * upload again for a reason that has nothing to do with why it stopped.
 */
function isParked(upload: LocalRecordingUpload): boolean {
  if (upload.status !== 'failed') return false;
  return upload.attempts >= MAX_AUTOMATIC_ATTEMPTS
    || NON_RETRYABLE_UPLOAD_CODES.has(upload.lastErrorCode ?? '');
}

/**
 * The server refuses to store this walkthrough's video because the workspace
 * has no room for the tickets it would become (TOADO-372). The wire value
 * `checkTicketCapacity` has always sent.
 */
export const TICKET_CAP_ERROR_CODE = 'FREE_MONTHLY_CAP_REACHED';

/**
 * Failures that resending cannot fix.
 *
 * Everything else here is transient by nature - a dropped connection, a
 * server restart, a laptop that slept - so the controller retries up to
 * MAX_AUTOMATIC_ATTEMPTS. A plan with no ticket capacity is not that: five
 * more attempts produce five more refusals, and the person waits through all
 * of them for an answer the first one already gave.
 *
 * Parked on the code, not by spending the attempt budget. The explicit
 * `retry()` clears the code, which un-parks it with its real attempt count
 * intact - so a walkthrough retried after an upgrade still tolerates the
 * ordinary network failures every other upload does.
 */
const NON_RETRYABLE_UPLOAD_CODES = new Set<string>([TICKET_CAP_ERROR_CODE]);

function errorMessage(error: unknown): string | undefined {
  if (error instanceof Error && error.message.trim()) return error.message.trim();
  return undefined;
}

export type RecordingUploadSnapshot = {
  uploads: LocalRecordingUpload[];
};

export type RecordingMasterUploadServer = {
  getUpload(recordingId: string, signal?: AbortSignal): Promise<{
    offset: number;
    totalBytes: number | null;
    status: string;
  }>;
  uploadPart(
    recordingId: string,
    body: Blob,
    input: {
      start: number;
      total: number;
      recordingPartIndex: number;
      recordingPartStartMs: number;
    },
    signal?: AbortSignal,
  ): Promise<{ offset: number }>;
  /**
   * The processing check. A rejection of the request's own `fetch` must reach
   * the controller marked with `markUnanswered` (`./requestAnswer`), as the web
   * client and the extension's authFetch do: an unmarked error is taken for an
   * answer and spends an attempt (TOADO-995). The mark does not survive being
   * passed between documents, so an adapter that proxies the call marks it on
   * the side that called `fetch`.
   */
  getRecording(recordingId: string, signal?: AbortSignal): Promise<RecordingServerSession>;
  retryClips(recordingId: string, signal?: AbortSignal): Promise<{ status: 'uploading' | 'ready' }>;
};

export type RecordingUploadControllerDependencies = {
  store: RecorderUploadStore;
  server: RecordingMasterUploadServer;
  waitForHighPriorityUploads?: () => Promise<void>;
  lock?: Pick<RecorderSettlementLock, 'runExclusive'>;
  signal?: RecorderUploadSignal;
  isOnline?: () => boolean;
  now?: () => Date;
  processingPollMs?: number;
  /** How long one part may take: `UPLOAD_PART_DEADLINE_MS` unless a test says otherwise. */
  partDeadlineMs?: number;
  /** How long any other request may take: `UPLOAD_REQUEST_DEADLINE_MS` unless a test says otherwise. */
  requestDeadlineMs?: number;
  /** The least a part is given once parts have been measured: `STALL_FLOOR_MS` unless a test says otherwise. */
  stallFloorMs?: number;
  /** When the next automatic attempt starts: `automaticRetryDelayMs` unless a test says otherwise. */
  retryDelayMs?: (attempts: number) => number;
  /** Part sizes, largest and smallest: the module's constants unless a test says otherwise. */
  partBytes?: { max: number; min: number };
};

type ChunkSpan = {
  chunk: RecordingChunk;
  startOffset: number;
  endOffset: number;
  recordingPartStartMs: number;
};

/**
 * Runs one upload request within `ms` (TOADO-991): past it, the request is
 * aborted and this rejects with `upload_part_timeout`, which the controller
 * counts as a failed attempt and retries. The run's own abort still rejects as
 * that abort, so cancelling is never read as a timeout.
 *
 * The run's signal is joined by a listener this removes, not `AbortSignal.any`
 * (review round 1): that is missing from older browsers, where it threw on
 * every part and spent the attempts, and it leaves one listener per part on a
 * signal that lives for the whole upload.
 */
export async function withPartDeadline<T>(
  call: (signal: AbortSignal) => Promise<T>,
  signal: AbortSignal,
  ms: number,
  /** What a request past its deadline fails as: a part's own code unless it is another request (review round 6). */
  timeout: { code: string; message: string } = { code: 'upload_part_timeout', message: 'An upload part did not finish before its deadline.' },
): Promise<T> {
  const request = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    request.abort();
  }, ms);
  const cancel = () => request.abort();
  if (signal.aborted) request.abort();
  else signal.addEventListener('abort', cancel, { once: true });
  try {
    return await call(request.signal);
  } catch (error) {
    if (timedOut && !signal.aborted) {
      throw Object.assign(new Error(timeout.message), { code: timeout.code });
    }
    throw error;
  } finally {
    clearTimeout(timer);
    signal.removeEventListener('abort', cancel);
  }
}

/** A stored time per byte that can be used: the row is stored, and can be old or damaged. */
function validRate(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

/** Refused sizes sent again at one offset before it counts as a failed attempt (TOADO-991, review round 4). */
const MAX_CONFLICT_PROBES = 8;

/** The sizes this row says were sent at `offset`, the plausible ones only: the row is stored, and can be old. */
function triedSizesAt(upload: LocalRecordingUpload, offset: number): number[] {
  const tried = upload.partTried;
  if (!tried || tried.start !== offset || !Array.isArray(tried.sizes)) return [];
  // The newest, which is what was last sent there (review round 5).
  return tried.sizes.filter((size) => Number.isSafeInteger(size) && size > 0).slice(-MAX_CONFLICT_PROBES);
}

/** The size of the reservation a refusal names at `offset`, when it names one that starts there (review round 4). */
function reservedSizeAt(error: unknown, offset: number): number | undefined {
  const end = typeof error === 'object' && error !== null ? (error as { reservedEnd?: unknown }).reservedEnd : undefined;
  return typeof end === 'number' && Number.isSafeInteger(end) && end >= offset ? end - offset + 1 : undefined;
}

/** The smaller part size a timed-out part asks for next, carried on its error (review round 3). */
function partBytesFrom(error: unknown): number | undefined {
  const value = typeof error === 'object' && error !== null ? (error as { partBytes?: unknown }).partBytes : undefined;
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;
}

/** How many parts in a row have timed out under a measured deadline, as a stored row says: none unless it is a small whole number (review round 2). */
function stretchOf(value: unknown): number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value > 0 ? Math.min(MAX_STRETCH, value) : 0;
}

/** The stretch a timed-out part leaves for the next attempt, carried on its error like its size (review round 2). */
function partStretchFrom(error: unknown): number | undefined {
  const value = typeof error === 'object' && error !== null ? (error as { partStretch?: unknown }).partStretch : undefined;
  return value === undefined ? undefined : stretchOf(value);
}

function errorCode(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'code' in error) {
    const code = (error as { code?: unknown }).code;
    if (typeof code === 'string' && code.length > 0) return code;
  }
  if (error instanceof DOMException && error.name === 'AbortError') return 'upload_aborted';
  return 'upload_failed';
}

/**
 * Whether a request ended without the server answering it: its own deadline
 * ran out, or its `fetch` rejected, which the adapters mark at the call
 * itself (`markUnanswered`, review round 1). Not a refusal or a server error,
 * and not a response whose body then failed to read: those are answers. A
 * body that stalls past the deadline after its headers is read as no answer;
 * the processing wait still bounds it.
 */
function checkGotNoAnswer(error: unknown): boolean {
  return errorCode(error) === 'upload_request_timeout' || requestWentUnanswered(error);
}

/** One promise for every caller of a queued retry, and its release (TOADO-995, review round 3). */
interface RetryWaiter {
  done: Promise<void>;
  release: () => void;
}

function retryWaiter(): RetryWaiter {
  let release!: () => void;
  const done = new Promise<void>((resolve) => { release = resolve; });
  return { done, release };
}

function ignore(): void {}

function abortError(): DOMException {
  return new DOMException('Recording upload aborted.', 'AbortError');
}

async function waitForAbortable(operation: Promise<void>, signal: AbortSignal): Promise<void> {
  if (signal.aborted) throw abortError();
  let removeAbort = () => {};
  const aborted = new Promise<never>((_resolve, reject) => {
    const onAbort = () => reject(abortError());
    signal.addEventListener('abort', onAbort, { once: true });
    removeAbort = () => signal.removeEventListener('abort', onAbort);
  });
  try {
    await Promise.race([operation, aborted]);
  } finally {
    removeAbort();
  }
}

function orderedChunkSpans(chunks: RecordingChunk[]): { spans: ChunkSpan[]; totalBytes: number } {
  const ordered = [...chunks].sort((a, b) => a.sequence - b.sequence);
  if (ordered[0] && (ordered[0].sequence !== 0 || ordered[0].partIndex !== 0)) {
    throw Object.assign(new Error('Local recording starts after its first chunk.'), { code: 'chunk_gap' });
  }
  const partStarts = new Map<number, number>();
  for (const chunk of ordered) {
    if (!partStarts.has(chunk.partIndex)) partStarts.set(chunk.partIndex, chunk.startMs);
  }
  let offset = 0;
  const spans = ordered.map((chunk, index) => {
    if (chunk.size <= 0 || chunk.blob.size !== chunk.size) {
      throw Object.assign(new Error('A local recording chunk has an invalid size.'), { code: 'chunk_corrupt' });
    }
    if (index > 0) {
      const previous = ordered[index - 1]!;
      if (chunk.sequence !== previous.sequence + 1
        || chunk.startMs !== previous.endMs
        || chunk.partIndex < previous.partIndex
        || chunk.partIndex > previous.partIndex + 1) {
        throw Object.assign(new Error('Local recording chunks are out of order.'), { code: 'chunk_gap' });
      }
    }
    const span = {
      chunk,
      startOffset: offset,
      endOffset: offset + chunk.size,
      recordingPartStartMs: partStarts.get(chunk.partIndex)!,
    };
    offset = span.endOffset;
    return span;
  });
  return { spans, totalBytes: offset };
}

function uploadBodyAt(spans: ChunkSpan[], offset: number, maxBytes: number): {
  body: Blob;
  recordingPartIndex: number;
  recordingPartStartMs: number;
} {
  const first = spans.find((span) => span.startOffset <= offset && offset < span.endOffset);
  if (!first) throw Object.assign(new Error('Upload offset is outside local chunks.'), { code: 'chunk_gap' });
  const partEnd = spans
    .filter((span) => span.chunk.partIndex === first.chunk.partIndex)
    .at(-1)!.endOffset;
  const end = Math.min(partEnd, offset + maxBytes);
  const slices: Blob[] = [];
  for (const span of spans) {
    if (span.endOffset <= offset || span.startOffset >= end) continue;
    const from = Math.max(0, offset - span.startOffset);
    const to = Math.min(span.chunk.size, end - span.startOffset);
    slices.push(span.chunk.blob.slice(from, to, 'video/webm'));
  }
  const body = new Blob(slices, { type: 'video/webm' });
  if (body.size !== end - offset || body.size <= 0) {
    throw Object.assign(new Error('Upload part could not be reconstructed.'), { code: 'chunk_gap' });
  }
  return {
    body,
    recordingPartIndex: first.chunk.partIndex,
    recordingPartStartMs: first.recordingPartStartMs,
  };
}

export class RecordingUploadController {
  private readonly listeners = new Set<() => void>();
  private snapshot: RecordingUploadSnapshot = { uploads: [] };
  private readonly running = new Map<string, Promise<void>>();
  private readonly aborts = new Map<string, AbortController>();
  private readonly pollTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly processingPolls = new Map<string, number>();
  private readonly lock: Pick<RecorderSettlementLock, 'runExclusive'>;
  /**
   * Moves on every dispose (review rounds 4 and 5). Work that waited on a
   * read checks it has not moved, so nothing started before a dispose goes on
   * after it; and a connect after a dispose (React runs an effect's setup,
   * cleanup and setup again in development) gets a working controller back.
   */
  private lifecycle = 0;
  private disposed = false;
  /** Starts asked for while an aborted run still held the slot (review round 6). */
  private readonly restartWhenFree = new Set<string>();
  /**
   * Retries asked for (a person's press, or the line coming back) and not yet
   * carried out, each with the number of its latest ask (`asks`, TOADO-995). A retry is
   * the person's or the network's say-so, not a save's signal, so it is never
   * dropped: one asked while a run holds the slot is made the moment that run
   * lets go, rather than lost or left to the backoff; and one a dispose stops
   * before it is done is kept, and made again by the next `resumeOwner`
   * (React's development double effect disposes and reconnects this same
   * controller). A remount that builds a new controller cannot know of it.
   * Cleared with the owner.
   */
  private readonly retriesAsked = new Map<string, number>();
  /**
   * Where each ask's number comes from: one count for the controller's life,
   * never reset, so an ask made after the map was cleared (another owner
   * resumed meanwhile) is still later than a retry already running when it
   * was cleared, and that retry's finally does not take it for its own
   * (review round 4).
   */
  private asks = 0;
  /**
   * Callers of a retry asked while a run held the slot (review round 2).
   * `retry()` resolves once its transition is saved, and for a queued ask
   * that is the queued retry's, not the earlier run's: resolving with the
   * earlier run let a caller refetch before the retry it asked for had moved
   * the row on, cache the failure, and stop polling. One promise per queued
   * ask, however often it is pressed (review round 3). Let go with nothing
   * more to wait for when the ask is dropped (clearOwner, a discard, another
   * owner) or the controller is disposed, so no caller waits for ever: while
   * it is queued, and once it is under way too (`retriesUnderWay`).
   */
  private readonly queuedRetries = new Map<string, RetryWaiter>();
  /** Queued retries being made now, until their transition is saved (review round 3). */
  private readonly retriesUnderWay = new Map<string, Set<RetryWaiter>>();
  private readonly signal: RecorderUploadSignal;
  private readonly ownsSignal: boolean;
  private ownerId: string | null = null;
  private unsubscribeRecorder: (() => void) | null = null;
  private unsubscribeSignal: (() => void) | null = null;

  constructor(
    private readonly recorder: RecorderController,
    private readonly dependencies: RecordingUploadControllerDependencies,
  ) {
    this.lock = dependencies.lock ?? new BrowserRecorderSettlementLock();
    this.signal = dependencies.signal ?? new BrowserRecorderUploadSignal();
    this.ownsSignal = dependencies.signal === undefined;
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): RecordingUploadSnapshot => this.snapshot;

  connect(): () => void {
    this.disposed = false;
    if (!this.unsubscribeRecorder) this.unsubscribeRecorder = this.recorder.subscribe(this.observeRecorder);
    if (!this.unsubscribeSignal) this.unsubscribeSignal = this.signal.subscribe(this.observeExternalUpload);
    this.observeRecorder();
    return () => this.disconnect();
  }

  private disconnect(): void {
    this.unsubscribeRecorder?.();
    this.unsubscribeRecorder = null;
    this.unsubscribeSignal?.();
    this.unsubscribeSignal = null;
  }

  dispose(): void {
    // Nothing scheduled afterwards, including by a store read still in flight (review round 4).
    this.disposed = true;
    this.letRetryWaitersGo();
    this.restartWhenFree.clear();
    this.lifecycle += 1;
    this.disconnect();
    this.aborts.forEach((controller) => controller.abort());
    this.aborts.clear();
    this.pollTimers.forEach((timer) => clearTimeout(timer));
    this.pollTimers.clear();
    this.processingPolls.clear();
    if (this.ownsSignal) this.signal.dispose();
    this.listeners.clear();
  }

  private publish(upload: LocalRecordingUpload): void {
    const uploads = this.snapshot.uploads.some((row) => row.recordingId === upload.recordingId)
      ? this.snapshot.uploads.map((row) => row.recordingId === upload.recordingId ? upload : row)
      : [...this.snapshot.uploads, upload];
    this.snapshot = { uploads };
    this.listeners.forEach((listener) => listener());
  }

  private diagnostic(code: string, recordingId: string): void {
    if (typeof window === 'undefined' || typeof CustomEvent === 'undefined') return;
    window.dispatchEvent(new CustomEvent('vd:recorder-diagnostic', {
      detail: { code, phase: 'upload', recordingId },
    }));
  }

  /**
   * A request beside the parts, within its own deadline (review round 6): the
   * offset check, the clip retry and the processing check were the requests a
   * starved line could leave waiting for good once the parts had a deadline.
   */
  private request<T>(call: (signal: AbortSignal) => Promise<T>, signal: AbortSignal, deadlineMs?: number): Promise<T> {
    const deadline = this.dependencies.requestDeadlineMs ?? UPLOAD_REQUEST_DEADLINE_MS;
    return withPartDeadline(call, signal, deadlineMs === undefined ? deadline : Math.min(deadline, deadlineMs), {
      code: 'upload_request_timeout',
      message: 'An upload request did not finish before its deadline.',
    });
  }

  private nowIso(): string {
    return (this.dependencies.now?.() ?? new Date()).toISOString();
  }

  private isOnline(): boolean {
    return this.dependencies.isOnline?.() ?? (typeof navigator === 'undefined' || navigator.onLine);
  }

  private async save(upload: LocalRecordingUpload): Promise<void> {
    await this.dependencies.store.saveUpload(upload);
    this.publish(upload);
    this.signal.publish(upload.recordingId);
  }

  private observeExternalUpload = (recordingId: string): void => {
    const ownerId = this.ownerId;
    if (!ownerId) return;
    const lifecycle = this.lifecycle;
    void this.dependencies.store.getUpload(recordingId).then((upload) => {
      if (this.ownerId !== ownerId || this.lifecycle !== lifecycle) return;
      if (!upload) {
        this.snapshot = {
          uploads: this.snapshot.uploads.filter((row) => row.recordingId !== recordingId),
        };
        this.listeners.forEach((listener) => listener());
        return;
      }
      if (upload.ownerId !== ownerId) return;
      this.publish(upload);
      if (upload.status !== 'ready' && !isParked(upload)) {
        this.start(recordingId);
      }
    }).catch(() => {
      this.diagnostic('recording_upload_recovery_unavailable', recordingId);
    });
  };

  async resumeOwner(ownerId: string): Promise<void> {
    // Another person's asks are not this one's, though no clearOwner came between (review round 1).
    if (this.ownerId !== null && this.ownerId !== ownerId) {
      this.retriesAsked.clear();
      this.letRetryWaitersGo();
    }
    this.ownerId = ownerId;
    const [uploads, sessions] = await Promise.all([
      this.dependencies.store.listUploads(ownerId),
      this.dependencies.store.listRecoverable(ownerId, this.dependencies.now?.() ?? new Date()),
    ]);
    if (this.ownerId !== ownerId) return;
    this.snapshot = { uploads };
    this.listeners.forEach((listener) => listener());
    for (const session of sessions) {
      // Re-read per recording: recovering a long list of them is not
      // instantaneous, and a sign-out partway through it must stop the rest.
      if (this.ownerId !== ownerId) return;
      if (session.status === 'completed' && !uploads.some((upload) => upload.recordingId === session.id)) {
        await this.ensureUpload(session).catch((error) => {
          this.diagnostic(`recording_${errorCode(error)}`, session.id);
        });
      }
    }
    if (this.ownerId !== ownerId) return;
    for (const upload of this.snapshot.uploads) {
      if (upload.status === 'ready') continue;
      // A retry asked for before a dispose stopped it: made now, parked or not (TOADO-995).
      if (this.retriesAsked.has(upload.recordingId)) {
        this.makeAskedRetry(upload.recordingId);
        continue;
      }
      if (isParked(upload)) continue;
      this.start(upload.recordingId);
    }
  }

  clearOwner(): void {
    this.ownerId = null;
    // Another person's retries are not theirs to carry out (TOADO-995), nor a restart one of theirs
    // asked for (security review, 30 Oct): cleared as dispose clears it, though start() checks the owner too.
    this.retriesAsked.clear();
    this.restartWhenFree.clear();
    this.letRetryWaitersGo();
    this.aborts.forEach((controller) => controller.abort());
    this.aborts.clear();
    this.pollTimers.forEach((timer) => clearTimeout(timer));
    this.pollTimers.clear();
    this.processingPolls.clear();
    this.snapshot = { uploads: [] };
    this.listeners.forEach((listener) => listener());
  }

  async prepareForSignOut(): Promise<void> {
    // Fence new signal, poll, and retry work before waiting on operations
    // already in flight. The aborts then make those operations converge.
    this.ownerId = null;
    this.aborts.forEach((controller) => controller.abort());
    // Both: an upload that is running, and a preparation that is about to
    // write the row for one.
    await Promise.allSettled([...this.running.values(), ...this.preparing.values()]);
    this.clearOwner();
  }

  async prepareDiscard(recordingId: string): Promise<void> {
    /**
     * Set before the first await, and never cleared.
     *
     * A discard that begins while the upload is still being prepared has
     * nothing to abort yet: the preparation finishes, starts the upload it
     * was preparing, and the discard then waits behind bytes being sent for
     * a walkthrough the person asked to throw away - long enough, now that
     * walkthroughs commit themselves, for it to become tickets first.
     */
    this.discarding.add(recordingId);
    // Nothing asked for it is carried out once it is being thrown away (TOADO-995).
    this.retriesAsked.delete(recordingId);
    this.letRetryWaitersGo(recordingId);
    this.aborts.get(recordingId)?.abort();
    const timer = this.pollTimers.get(recordingId);
    if (timer) clearTimeout(timer);
    this.pollTimers.delete(recordingId);
    await this.running.get(recordingId)?.catch(() => {});
    // A discard racing the preparation of the same recording: without this,
    // the row is deleted and then written again by the work that was already
    // reading its chunks.
    await this.preparing.get(recordingId)?.catch(() => {});
    await this.lock.runExclusive(`upload:${recordingId}`, async () => {
      await this.dependencies.store.deleteUpload(recordingId);
    });
    this.signal.publish(recordingId);
    this.snapshot = {
      uploads: this.snapshot.uploads.filter((upload) => upload.recordingId !== recordingId),
    };
    this.listeners.forEach((listener) => listener());
  }

  private observeRecorder = (): void => {
    const recorder = this.recorder.getSnapshot();
    if (recorder.phase !== 'completed' || !recorder.session || !this.ownerId) return;
    if (recorder.session.ownerId !== this.ownerId) return;
    const session = recorder.session;
    /**
     * Tracked like an upload, because sign-out waits on what is tracked.
     *
     * Reading the chunks of a long walkthrough takes real time, and the row
     * this writes afterwards used to be able to land after sign-out had
     * purged the account's storage: a pending upload for a recording whose
     * chunks are gone, re-published into the signed-out snapshot.
     */
    const ensuring = this.ensureUpload(session)
      .then(() => { this.start(session.id); })
      .catch((error) => this.diagnostic(`recording_${errorCode(error)}`, session.id))
      .finally(() => {
        if (this.preparing.get(session.id) === ensuring) this.preparing.delete(session.id);
      });
    this.preparing.set(session.id, ensuring);
  };

  /**
   * Preparation in flight, which is not the same as an upload in flight.
   *
   * Sign-out has to wait for this - reading a long walkthrough's chunks takes
   * real time, and the row written afterwards must not land after the storage
   * it describes has been purged. It is deliberately NOT in `running`, which
   * is what `start` checks to avoid two uploads of one recording: putting it
   * there made a freshly stopped recording prepare its upload and then refuse
   * to start it, because the preparation was still listed as the operation in
   * progress. The upload then waited for the next sign-in.
   */
  private readonly preparing = new Map<string, Promise<void>>();
  /** Recordings somebody has asked to throw away, so nothing starts sending them. */
  private readonly discarding = new Set<string>();

  private async ensureUpload(session: LocalRecordingSession): Promise<LocalRecordingUpload> {
    const existing = await this.dependencies.store.getUpload(session.id);
    if (existing) {
      if (existing.ownerId !== session.ownerId) {
        throw Object.assign(new Error('Upload owner does not match the recording owner.'), { code: 'upload_owner_mismatch' });
      }
      return existing;
    }
    const chunks = await this.dependencies.store.readChunks(session.id);
    if (chunks.some((chunk) => chunk.ownerId !== session.ownerId || chunk.sessionId !== session.id)) {
      throw Object.assign(new Error('Recording chunks belong to another owner.'), { code: 'upload_owner_mismatch' });
    }
    const { totalBytes } = orderedChunkSpans(chunks);
    if (totalBytes <= 0) {
      throw Object.assign(new Error('The recording has no persisted chunks.'), { code: 'upload_chunks_missing' });
    }
    // Checked after the reads above, not only before them: sign-out clears the
    // owner while they are in flight, and a row written after that is a row
    // nothing will ever finish.
    if (this.ownerId !== session.ownerId) {
      throw Object.assign(new Error('Sign-out began while the upload was being prepared.'),
        { code: 'upload_aborted' });
    }
    const timestamp = this.nowIso();
    const upload: LocalRecordingUpload = {
      recordingId: session.id,
      ownerId: session.ownerId,
      status: 'pending',
      offset: 0,
      totalBytes,
      attempts: 0,
      createdAt: timestamp,
      updatedAt: timestamp,
    };
    await this.save(upload);
    return upload;
  }

  private start(recordingId: string): void {
    if (this.disposed || !this.ownerId) return;
    if (this.running.has(recordingId)) {
      // A run stopped by a dispose can still hold the slot after a connect: this
      // start is then made when it lets go, rather than lost (review round 6). A
      // run that was not stopped does the work itself, and the ask is dropped.
      this.restartWhenFree.add(recordingId);
      return;
    }
    // Somebody asked for this recording to go away while it was being
    // prepared. Starting its upload now would send bytes for a walkthrough
    // that is being deleted, and hold the discard behind them.
    if (this.discarding.has(recordingId)) return;
    // Started some other way (another window's signal, a sweep, sign-in): an
    // attempt or poll waiting to start is this one now (review round 2).
    const waiting = this.pollTimers.get(recordingId);
    if (waiting) clearTimeout(waiting);
    this.pollTimers.delete(recordingId);
    const abort = new AbortController();
    this.aborts.set(recordingId, abort);
    const owner = this.ownerId;
    let failed: LocalRecordingUpload | null = null;
    const operation = this.lock.runExclusive(`upload:${recordingId}`, async () => {
      /**
       * Held for as long as this tab is writing, and released when it stops.
       *
       * Sign-out deletes this account's recordings once it can prove nothing
       * is writing them, and this is the proof: it takes the same lock
       * exclusively, which the browser grants exactly when the last writer
       * lets go. A tab that is only finishing an upload holds no recorder
       * lease and would otherwise be invisible to it.
       */
      const writing = await holdRecorderWriterLease(owner);
      try {
        const upload = await this.dependencies.store.getUpload(recordingId);
        // Stopped while it waited for the lock or the row (a dispose, a sign-out,
        // a discard): it does not go on to the network (review round 5).
        if (abort.signal.aborted) return;
        if (!upload || upload.ownerId !== this.ownerId) return;
        // Another tab may have advanced the authoritative row while this tab
        // waited for the Web Lock. Always replace the local snapshot before
        // deciding whether more work is needed.
        this.publish(upload);
        if (
          upload.status === 'ready'
          || isParked(upload)
        ) return;
        if (upload.status === 'processing') {
          await this.refreshProcessing(upload, abort.signal);
          return;
        }
        if (upload.status === 'cleanup') {
          await this.finishLocalCleanup(upload, abort.signal);
          return;
        }
        /**
         * Failed after the master landed, which is a different failure.
         *
         * Its bytes are all on the server and its local chunks were deleted
         * when they got there, so there is nothing to upload: what failed was
         * the clip work afterwards, and what it needs is to be asked for
         * again. Sending it through `upload()` instead reads no chunks, finds
         * a total of zero against a recording that had bytes, and reports
         * that the local recording changed length - a lie that spends one of
         * the automatic attempts, every time, until the walkthrough is parked
         * for good. The manual retry has always known this; the automatic
         * resumes did not.
         */
        if (upload.status === 'failed' && upload.offset === upload.totalBytes) {
          await this.resumeAfterMaster(upload, abort.signal);
          return;
        }
        await this.upload(upload, abort.signal);
      } catch (error) {
        failed = await this.recordFailure(recordingId, error);
      } finally {
        writing();
      }
    }).catch((error) => {
      this.diagnostic(`recording_${errorCode(error)}`, recordingId);
    }).finally(() => {
      this.running.delete(recordingId);
      if (this.aborts.get(recordingId) === abort) this.aborts.delete(recordingId);
      // Once the lock is let go and this run is no longer `running`, or the
      // attempt it starts would find this one still going and do nothing.
      // A start asked for meanwhile is made when this run was stopped (review rounds 6 to 8).
      // A run that failed on its own goes through the backoff instead: every save here
      // also signals, so an ask is almost always waiting, and honouring it would retry at once.
      const askedAgain = this.restartWhenFree.delete(recordingId);
      // A retry asked for while this ran is made now, not after the backoff; and one
      // asked while a dispose's stop still held the slot, once the controller is live
      // again, since the resume that asked it found this run and is gone (TOADO-995).
      if (this.retriesAsked.has(recordingId) && (!abort.signal.aborted || !this.disposed)) this.makeAskedRetry(recordingId);
      else if (!abort.signal.aborted) this.retryIfFailed(recordingId, failed);
      else if (askedAgain) this.start(recordingId);
    });
    this.running.set(recordingId, operation);
  }

  /**
   * Schedules the next attempt for a run that ended failed, whether it threw
   * (`failed`, the row it wrote) or wrote the failure itself, as watching the
   * clips does on a processing failure or timeout (review round 2): read from
   * the row as last saved here, so no path has to remember to hand it back.
   */
  private retryIfFailed(recordingId: string, failed: LocalRecordingUpload | null): void {
    if (failed) {
      this.scheduleAutomaticRetry(failed);
      return;
    }
    // From the store, not this window's snapshot, which can trail a row another
    // window wrote (review round 3).
    const lifecycle = this.lifecycle;
    void this.dependencies.store.getUpload(recordingId).then((row) => {
      if (this.lifecycle === lifecycle && row?.status === 'failed') this.scheduleAutomaticRetry(row);
    }).catch(() => undefined);
  }

  /** The part size to send in now: the row's, inside the bounds, or the largest. */
  private partSizeFor(upload: LocalRecordingUpload, largest: number, smallest: number): number {
    const stored = upload.partBytes;
    return typeof stored === 'number' && Number.isFinite(stored)
      ? Math.min(largest, Math.max(smallest, Math.floor(stored)))
      : largest;
  }

  private async upload(initial: LocalRecordingUpload, signal: AbortSignal): Promise<void> {
    if (!this.isOnline()) throw Object.assign(new Error('Browser is offline.'), { code: 'offline' });
    const chunks = await this.dependencies.store.readChunks(initial.recordingId);
    if (chunks.some((chunk) => chunk.ownerId !== initial.ownerId
      || chunk.sessionId !== initial.recordingId)) {
      throw Object.assign(new Error('Recording chunks belong to another owner.'), { code: 'upload_owner_mismatch' });
    }
    const { spans, totalBytes } = orderedChunkSpans(chunks);
    if (totalBytes !== initial.totalBytes) {
      throw Object.assign(new Error('Local recording length changed during upload.'), { code: 'upload_length_conflict' });
    }
    const remote = await this.request((requestSignal) => this.dependencies.server.getUpload(initial.recordingId, requestSignal), signal);
    if (remote.totalBytes !== null && remote.totalBytes !== totalBytes) {
      throw Object.assign(new Error('Server upload length does not match local recording.'), { code: 'upload_length_conflict' });
    }
    if (remote.offset > totalBytes) {
      throw Object.assign(new Error('Server upload offset exceeds the local recording.'), { code: 'upload_offset_conflict' });
    }
    const advanced = remote.offset > initial.offset;
    let upload: LocalRecordingUpload = {
      ...initial,
      status: 'uploading',
      offset: remote.offset,
      // The server holds more than this row knew, so the last attempt moved
      // bytes though its answer was lost: it was not one that moved nothing (review round 2).
      attempts: advanced ? 0 : initial.attempts,
      // A part landed, as surely as one whose answer came back: the stretch
      // goes with it, so the next part is not held for its doubled time (review round 3).
      ...(advanced ? { partStretch: undefined } : {}),
      // Every byte there: nothing is left to size or time, and no later path
      // carries this upload's sizes and times to its finished row (review round 3).
      ...(remote.offset === totalBytes ? { partBytes: undefined, partTried: undefined, partRates: undefined, partStretch: undefined } : {}),
      updatedAt: this.nowIso(),
      lastErrorCode: undefined, lastErrorMessage: undefined,
    };
    await this.save(upload);
    // Sizes the server has refused at the offset being sent, this run (review round 3).
    const conflicted = new Set<number>();
    // A size the next pass must send as it is, past the bounds, and how many
    // refused sizes have been sent again at this offset (review round 4).
    let forced: number | null = null;
    // The time per byte of the last few parts to land, carried on the row so a
    // run that starts again begins already knowing the line, if it measured it
    // lately (28 Oct). Durations are read from the real clock, which a test's
    // fixed `now` would make zero; a measurement's age from the controller's,
    // which is the one a row's times are written in.
    const clock = () => (this.dependencies.now?.() ?? new Date()).getTime();
    let recentRates = freshRates(upload.partRates, clock());
    let forcedSize: number | null = null;
    let probes = 0;
    while (upload.offset < totalBytes) {
      if (!this.isOnline()) throw Object.assign(new Error('Browser is offline.'), { code: 'offline' });
      if (this.dependencies.waitForHighPriorityUploads) {
        await waitForAbortable(this.dependencies.waitForHighPriorityUploads(), signal);
      }
      const largest = this.dependencies.partBytes?.max ?? CLIENT_UPLOAD_PART_BYTES;
      const smallest = this.dependencies.partBytes?.min ?? MIN_CLIENT_UPLOAD_PART_BYTES;
      const deadlineMs = this.dependencies.partDeadlineMs ?? UPLOAD_PART_DEADLINE_MS;
      const size = forced ?? this.partSizeFor(upload, largest, smallest);
      forcedSize = forced;
      forced = null;
      const part = uploadBodyAt(spans, upload.offset, size);
      // Aged again before every part, not only as the run starts: a window
      // suspended between parts can wake on another line (review round 2).
      recentRates = freshRates(recentRates, clock());
      const stretch = stretchOf(upload.partStretch);
      // The last automatic attempt, before any part of it has landed: the
      // fixed deadline, so a slow line the fixed deadline served is not parked
      // short of it (review round 3). That one only, not a person's retry
      // after parking (review round 4: MAX_STRETCH says why).
      const lastChance = upload.attempts === MAX_AUTOMATIC_ATTEMPTS - 1;
      /**
       * A size the server is holding is sent whatever the line, so it is given
       * as long as that many of the smallest parts would get (review rounds 5
       * and 6): it is the one size that can land here, and a line that can send
       * the smallest part within the deadline can send this in that multiple.
       * Never less than three deadlines, round 5's allowance (round 7), and never
       * more than twelve, two hours at the usual ten minutes: past that a line
       * this slow is parked like any other, and a person can retry it.
       */
      const deadlineFor = partDeadline({
        bytes: part.body.size,
        rates: recentRates,
        stretch: lastChance ? MAX_STRETCH : stretch,
        deadlineMs,
        floorMs: this.dependencies.stallFloorMs ?? STALL_FLOOR_MS,
        forcedDeadline: size === forcedSize ? deadlineMs * Math.min(12, Math.max(3, Math.ceil(part.body.size / smallest))) : null,
      });
      // Shorter than the fixed deadline only because this line was measured.
      const measuredDeadline = size !== forcedSize && deadlineFor < deadlineMs;
      forcedSize = null;
      const offset = upload.offset;
      /**
       * Every size sent at this offset, kept on the row before it is sent
       * (review round 3). The server reserves a part once it holds the whole
       * body, and a store that then fails leaves the reservation; only those
       * same bytes are taken there afterwards, and they are one of these.
       */
      const tried = triedSizesAt(upload, offset);
      if (!tried.includes(part.body.size)) {
        upload = { ...upload, partTried: { start: offset, sizes: [...tried, part.body.size] } };
        await this.save(upload);
      }
      const sentAt = Date.now();
      let result: { offset: number };
      try {
        result = await withPartDeadline(
          (partSignal) => this.dependencies.server.uploadPart(
            upload.recordingId,
            part.body,
            {
              start: offset,
              total: totalBytes,
              recordingPartIndex: part.recordingPartIndex,
              recordingPartStartMs: part.recordingPartStartMs,
            },
            partSignal,
          ),
          signal,
          deadlineFor,
        );
      } catch (error) {
        const code = errorCode(error);
        /**
         * Refused because the server holds a reservation here for other bytes
         * (review rounds 3 and 4): an earlier part whose body arrived and whose
         * store failed. Sent again at once, without counting an attempt: first
         * the range the server names, which covers a reservation this row has no
         * record of (another window, an older version); then the other sizes
         * already tried here, largest first. Each is judged by the body it makes,
         * so a size the bounds would change cannot be sent twice, and at most
         * eight are tried before it is a failed attempt like any other. Each
         * sends its whole body again, which a slow line pays for.
         */
        if (code === 'upload_part_conflict' && probes < MAX_CONFLICT_PROBES) {
          conflicted.add(part.body.size);
          const reserved = reservedSizeAt(error, offset);
          const candidates = [...(reserved !== undefined ? [reserved] : []), ...[...tried].sort((a, b) => b - a)];
          const next = candidates
            .map((candidate) => uploadBodyAt(spans, offset, candidate).body.size)
            .find((made) => !conflicted.has(made));
          if (next !== undefined) {
            probes += 1;
            forced = next;
            continue;
          }
        }
        /**
         * Too big for this line (review round 1): the server keeps no part of
         * a part, so a slow line that cannot send the whole of one inside the
         * deadline would restart the same bytes every attempt and never land
         * any. The next attempt sends half, down to the smallest, in whichever
         * window makes it: carried on the failure itself, so it is written in
         * the same save that records the attempt (review round 3).
         */
        if (code === 'upload_part_timeout') {
          // Timed out under a deadline this line's measurements set: the next
          // attempt's is doubled (MAX_STRETCH says why), so a line that slowed
          // lands within a few, and a person's retry is given as long.
          throw Object.assign(error as Error, {
            partBytes: Math.max(smallest, Math.floor(part.body.size / 2)),
            // At the smallest part, timed out again, halving cannot help, so the
            // next is given the fixed deadline (review round 3). Only a part asked
            // for at the smallest size and sent whole, after an earlier timeout: a
            // short piece at a part's end, or every part where the bounds are equal,
            // doubles like any other the first time (review round 4).
            ...(measuredDeadline ? { partStretch: stretch > 0 && size === smallest && part.body.size === size ? MAX_STRETCH : Math.min(MAX_STRETCH, stretch + 1) } : {}),
          });
        }
        throw error;
      }
      if (result.offset <= upload.offset || result.offset > totalBytes) {
        throw Object.assign(new Error('Upload did not advance.'), { code: 'upload_no_progress' });
      }
      // Landed inside a quarter of the fixed deadline: the line has room again, and
      // larger parts keep a long recording inside the server's part limit.
      const took = Math.max(1, Date.now() - sentAt);
      recentRates = [...recentRates, { at: clock(), msPerByte: took / part.body.size }].slice(-RECENT_RATES);
      const grown = size < largest && took < deadlineMs / 4 ? Math.min(largest, size * 2) : size;
      /**
       * A part landed, so the line works: the attempts start again from none.
       * The budget is for attempts in a row that move nothing, which is what
       * parking is for; a slow upload interrupted now and then is still moving
       * (review round 1).
       */
      // The last part: nothing is left to size or time (review round 4).
      const done = result.offset === totalBytes;
      upload = {
        ...upload,
        offset: result.offset,
        attempts: 0,
        partBytes: !done && grown < largest ? grown : undefined,
        partTried: undefined,
        partRates: done ? undefined : recentRates,
        // A part landed: the deadline follows the line's measured speed again.
        partStretch: undefined,
        updatedAt: this.nowIso(),
      };
      conflicted.clear();
      probes = 0;
      await this.save(upload);
      this.diagnostic('recording_upload_part_acknowledged', upload.recordingId);
    }
    await this.request((requestSignal) => this.dependencies.server.retryClips(upload.recordingId, requestSignal), signal);
    const processing: LocalRecordingUpload = {
      ...upload,
      partBytes: undefined,
      partTried: undefined,
      partRates: undefined,
      partStretch: undefined,
      status: 'cleanup',
      updatedAt: this.nowIso(),
    };
    await this.save(processing);
    await this.finishLocalCleanup(processing, signal);
  }

  /**
   * Carry on from a master the server has already acknowledged.
   *
   * Three ways forward, and which one depends on what is still here: chunks
   * left behind mean the cleanup never finished, so finish it; no chunks and
   * a server that says the clips are ready means done; no chunks and clips
   * still outstanding means go back to watching the processing.
   *
   * The first of those deletes the only local copy of somebody's walkthrough,
   * so the precondition is asserted rather than assumed. `offset` is never a
   * local count: it is set from the server's HEAD and advanced by each part's
   * acknowledged offset, so `offset === totalBytes` is the server saying it
   * holds every byte. Chunks still present alongside that mean the delete
   * that follows an acknowledged master did not run, which is this path.
   */
  /**
   * Write down why an upload stopped, wherever it stopped.
   *
   * Shared by the automatic run and the explicit retry, because it used to be
   * written out only in the first. An explicit retry whose reconciliation was
   * refused - which is exactly what a walkthrough gets from the second
   * capacity check, with every byte already on the server - left a row that
   * said nothing had gone wrong. Nothing was parked, so the automatic sweeps
   * picked it up and reproduced the refusal, and the person was told nothing.
   */
  private async recordFailure(recordingId: string, error: unknown): Promise<LocalRecordingUpload | null> {
    const upload = await this.dependencies.store.getUpload(recordingId).catch(() => null);
    if (!upload || upload.ownerId !== this.ownerId || errorCode(error) === 'upload_aborted') return null;
    if (upload.status === 'ready') {
      this.publish(upload);
      return null;
    }
    const code = errorCode(error);
    const message = NON_RETRYABLE_UPLOAD_CODES.has(code) ? errorMessage(error) : undefined;
    const partStretch = partStretchFrom(error);
    const failed: LocalRecordingUpload = {
      ...upload,
      status: 'failed',
      attempts: upload.attempts + 1,
      updatedAt: this.nowIso(),
      lastErrorCode: code,
      ...(message ? { lastErrorMessage: message } : { lastErrorMessage: undefined }),
      ...(partBytesFrom(error) !== undefined ? { partBytes: partBytesFrom(error) } : {}),
      ...(partStretch !== undefined ? { partStretch } : {}),
    };
    const saved = await this.save(failed).then(() => true, () => false);
    this.diagnostic(`recording_${failed.lastErrorCode}`, recordingId);
    return saved ? failed : null;
  }

  /**
   * The next attempt, by itself, after a failure that another attempt can fix
   * (TOADO-991, review round 1). Nothing else asks in a window of its own: the
   * cross-tab signal reaches only other windows, and `online` never fires on a
   * line that is slow rather than gone. Not for a parked upload, and not for an
   * offline one, which `online` brings back without spending an attempt.
   *
   * On `pollTimers`, so every teardown that stops a poll (dispose, sign-out,
   * discard) stops this too; an upload is never polled and retried at once.
   */
  private scheduleAutomaticRetry(upload: LocalRecordingUpload): void {
    if (this.disposed || isParked(upload) || upload.lastErrorCode === 'offline') return;
    const { recordingId, ownerId } = upload;
    if (this.pollTimers.has(recordingId) || this.ownerId !== ownerId) return;
    const delay = (this.dependencies.retryDelayMs ?? automaticRetryDelayMs)(upload.attempts);
    const lifecycle = this.lifecycle;
    const timer = setTimeout(() => {
      // Only its own entry: another may hold the slot by now (review round 2).
      if (this.pollTimers.get(recordingId) !== timer) return;
      this.pollTimers.delete(recordingId);
      if (this.lifecycle !== lifecycle || this.ownerId !== ownerId || this.discarding.has(recordingId)) return;
      this.start(recordingId);
    }, delay);
    this.pollTimers.set(recordingId, timer);
  }

  private async resumeAfterMaster(upload: LocalRecordingUpload, signal: AbortSignal): Promise<void> {
    if (upload.offset !== upload.totalBytes) {
      throw Object.assign(new Error('Resume after master called before the server had every byte.'),
        { code: 'upload_offset_conflict' });
    }
    // Every byte is there: sizes and times a row kept from before are dropped
    // here, whichever way it came (review round 4).
    if (upload.partBytes !== undefined || upload.partTried !== undefined
      || upload.partRates !== undefined || upload.partStretch !== undefined) {
      upload = { ...upload, partBytes: undefined, partTried: undefined, partRates: undefined, partStretch: undefined };
      await this.save(upload);
    }
    /**
     * What the server did with it, before asking it to do anything.
     *
     * A walkthrough now files its own tickets (TOADO-325), so by the time a
     * client comes back to an interrupted upload the session may already be
     * committed. Asking such a session to cut its clips again is refused with
     * a 409, which this path used to take as a failure: another attempt
     * spent, the local chunks kept forever, and a walkthrough whose tickets
     * are already on the board reported as failed.
     */
    const session = await this.request((requestSignal) => this.dependencies.server.getRecording(upload.recordingId, requestSignal), signal);
    if (session.status === 'committed' || session.status === 'discarded') {
      await this.dependencies.store.deleteChunks(upload.recordingId);
      if (session.status === 'discarded') {
        await this.dependencies.store.deleteUpload(upload.recordingId);
        this.snapshot = {
          uploads: this.snapshot.uploads.filter((row) => row.recordingId !== upload.recordingId),
        };
        this.listeners.forEach((listener) => listener());
        return;
      }
      await this.save({
        ...upload, status: 'ready', updatedAt: this.nowIso(), lastErrorCode: undefined, lastErrorMessage: undefined,
      });
      return;
    }
    const chunks = await this.dependencies.store.readChunks(upload.recordingId);
    const result = await this.request((requestSignal) => this.dependencies.server.retryClips(upload.recordingId, requestSignal), signal);
    if (chunks.length > 0) {
      await this.finishLocalCleanup(
        { ...upload, status: 'cleanup', updatedAt: this.nowIso(), lastErrorCode: undefined, lastErrorMessage: undefined },
        signal,
      );
      return;
    }
    if (result.status === 'ready') {
      await this.save({ ...upload, status: 'ready', updatedAt: this.nowIso(), lastErrorCode: undefined, lastErrorMessage: undefined });
      return;
    }
    const processing: LocalRecordingUpload = {
      ...upload,
      status: 'processing',
      updatedAt: this.nowIso(),
      lastErrorCode: undefined, lastErrorMessage: undefined,
    };
    await this.save(processing);
    await this.refreshProcessing(processing, signal);
  }

  private async finishLocalCleanup(upload: LocalRecordingUpload, signal: AbortSignal): Promise<void> {
    await this.dependencies.store.deleteChunks(upload.recordingId);
    const processing: LocalRecordingUpload = {
      ...upload,
      status: 'processing',
      updatedAt: this.nowIso(),
      lastErrorCode: undefined, lastErrorMessage: undefined,
    };
    await this.save(processing);
    this.diagnostic('recording_upload_acknowledged', upload.recordingId);
    await this.refreshProcessing(processing, signal);
  }

  private async refreshProcessing(upload: LocalRecordingUpload, signal: AbortSignal): Promise<void> {
    let session: Awaited<ReturnType<RecordingMasterUploadServer['getRecording']>>;
    try {
      // Within what is left of the processing wait, so a check that never
      // answers ends near it rather than a whole deadline past (review round 1).
      session = await this.request(
        (requestSignal) => this.dependencies.server.getRecording(upload.recordingId, requestSignal),
        signal,
        Math.max(LAST_PROCESSING_CHECK_MS, this.processingWaitLeft(upload)),
      );
    } catch (error) {
      /**
       * A check that never got an answer is not a failed attempt (TOADO-995).
       * Its deadline ran out, or the request never reached the server: the
       * server has said nothing about the walkthrough, so the check is asked
       * again on its own schedule, within the processing wait, and spends none
       * of the automatic attempts. Five of those on a starved line used to
       * park a walkthrough the server was processing fine. An answer the
       * server gave, a refusal or an error, still counts.
       */
      if (signal.aborted || !checkGotNoAnswer(error)) throw error;
      // Past the wait, it ends as a wait past its limit does, not as this request's failure (review round 5).
      if (this.processingWaitIsOver(upload)) await this.recordProcessingTimeout(upload);
      else this.scheduleProcessingPoll(upload.recordingId);
      return;
    }
    if (session.masterUploadStatus === 'ready'
      && (session.segments ?? []).every((segment) => segment.droppedAt || segment.clipUploadStatus === 'ready')) {
      await this.save({ ...upload, status: 'ready', updatedAt: this.nowIso(), lastErrorCode: undefined, lastErrorMessage: undefined });
      this.processingPolls.delete(upload.recordingId);
      this.diagnostic('recording_clips_ready', upload.recordingId);
      return;
    }
    if (session.masterUploadStatus === 'failed'
      || (session.segments ?? []).some((segment) => !segment.droppedAt && segment.clipUploadStatus === 'failed')) {
      await this.save({
        ...upload,
        status: 'failed',
        attempts: upload.attempts + 1,
        updatedAt: this.nowIso(),
        lastErrorCode: 'processing_failed',
      });
      this.processingPolls.delete(upload.recordingId);
      this.diagnostic('recording_processing_failed', upload.recordingId);
      return;
    }
    if (this.processingWaitIsOver(upload)) {
      await this.recordProcessingTimeout(upload);
      return;
    }
    this.scheduleProcessingPoll(upload.recordingId);
  }

  /** The processing waited for longer than it is: one attempt spent, and the next made on the backoff. */
  private async recordProcessingTimeout(upload: LocalRecordingUpload): Promise<void> {
    await this.save({
      ...upload,
      status: 'failed',
      attempts: upload.attempts + 1,
      updatedAt: this.nowIso(),
      lastErrorCode: 'processing_timeout',
    });
    this.processingPolls.delete(upload.recordingId);
    this.diagnostic('recording_processing_timeout', upload.recordingId);
  }

  /** Whether this walkthrough has been processing longer than it is waited for, counted from when processing began. */
  private processingWaitIsOver(upload: LocalRecordingUpload): boolean {
    return this.processingWaitLeft(upload) < 0;
  }

  /** What is left of the processing wait, in milliseconds: below zero once it is over, or when it began at no readable time. */
  private processingWaitLeft(upload: LocalRecordingUpload): number {
    const processingStarted = Date.parse(upload.updatedAt);
    if (!Number.isFinite(processingStarted)) return -1;
    return MAX_PROCESSING_WAIT_MS - ((this.dependencies.now?.() ?? new Date()).getTime() - processingStarted);
  }

  private scheduleProcessingPoll(recordingId: string): void {
    if (this.pollTimers.has(recordingId)) return;
    const poll = (this.processingPolls.get(recordingId) ?? 0) + 1;
    this.processingPolls.set(recordingId, poll);
    const base = this.dependencies.processingPollMs ?? DEFAULT_PROCESSING_POLL_MS;
    const delay = Math.min(MAX_PROCESSING_POLL_MS, base * (2 ** Math.min(4, poll - 1)));
    const timer = setTimeout(() => {
      this.pollTimers.delete(recordingId);
      this.start(recordingId);
    }, delay);
    this.pollTimers.set(recordingId, timer);
  }

  /**
   * Returns when the retry's state transition has been persisted.
   *
   * The resolved promise deliberately does NOT mean the upload finished: the
   * resumed transfer runs on after it, and awaiting that would block a UI
   * button for as long as the video takes. It means the record is no longer
   * `failed`, which is the point at which a caller can usefully refetch. A
   * void return made `await controller.retry(id)` a no-op that resolved
   * immediately, so callers refetched the pre-retry state, cached it, and
   * showed a failure the server had already moved past.
   */
  retry(recordingId: string): Promise<void> {
    const existing = this.snapshot.uploads.find((upload) => upload.recordingId === recordingId);
    if (!existing || existing.ownerId !== this.ownerId || this.discarding.has(recordingId)) return Promise.resolve();
    // Counted before anything else, so an ask is never lost (TOADO-995). One
    // made while a run holds the slot is carried out when that run lets go.
    const asked = ++this.asks;
    this.retriesAsked.set(recordingId, asked);
    // Kept for the next resume: nothing here is waited on (review round 3).
    if (this.disposed) return Promise.resolve();
    // Made when the run holding the slot lets go, and waited on until then.
    if (this.running.has(recordingId)) return this.waitForAskedRetry(recordingId);
    // Pressed now, so an automatic attempt waiting to start is not also wanted.
    const waiting = this.pollTimers.get(recordingId);
    if (waiting) clearTimeout(waiting);
    this.pollTimers.delete(recordingId);
    const abort = new AbortController();
    this.aborts.set(recordingId, abort);
    let shouldStart = false;
    let failed: LocalRecordingUpload | null = null;
    const operation = this.lock.runExclusive(`upload:${recordingId}`, async () => {
      /**
       * Recorded inside the lock, like the automatic path's.
       *
       * Chained onto the promise `runExclusive` returns - which is where this
       * used to sit - it ran after the lock was released, so its read and its
       * write could straddle another tab's recovery: read `failed`, the other
       * tab saves `ready`, write `failed` back over it, and nothing automatic
       * touches the parked row again. The `status === 'ready'` guard inside
       * cannot see a transition that happens after its own read.
       */
      try {
        shouldStart = await this.attemptRetry(recordingId, abort.signal);
      } catch (error) {
        failed = await this.recordFailure(recordingId, error);
      }
    }).finally(() => {
      this.running.delete(recordingId);
      if (this.aborts.get(recordingId) === abort) this.aborts.delete(recordingId);
      // A start asked for while this held the slot is made now if this was stopped,
      // as a stopped run's is (review round 7).
      const askedAgain = this.restartWhenFree.delete(recordingId);
      // Carried out unless stopped: a stopped retry stays asked for, for the
      // next resume (TOADO-995). Asked again meanwhile, and this one failed:
      // that ask is made now rather than after the backoff.
      const askedSince = !abort.signal.aborted && (this.retriesAsked.get(recordingId) ?? 0) > asked;
      if (!abort.signal.aborted && !askedSince) this.retriesAsked.delete(recordingId);
      if (shouldStart && !abort.signal.aborted && this.ownerId === existing.ownerId) {
        // An ask made meanwhile is this start: its transition is saved.
        if (askedSince) {
          this.retriesAsked.delete(recordingId);
          this.letRetryWaitersGo(recordingId);
        }
        this.start(recordingId);
      } else if (askedSince || (abort.signal.aborted && !this.disposed && this.retriesAsked.has(recordingId))) {
        // Stopped, and the controller live again: the resume that would make the
        // kept ask found this still in the slot, so it is made here (TOADO-995).
        this.makeAskedRetry(recordingId);
      } else if (!abort.signal.aborted) {
        this.retryIfFailed(recordingId, failed);
      } else if (askedAgain) {
        this.start(recordingId);
      }
    });
    this.running.set(recordingId, operation);
    return operation;
  }

  /**
   * One explicit retry attempt, with the upload lock already held.
   *
   * Returns whether the transfer should now be started, and throws so the
   * caller can record the failure under that same lock.
   */
  private async attemptRetry(recordingId: string, signal: AbortSignal): Promise<boolean> {
    let upload = await this.dependencies.store.getUpload(recordingId);
    // Stopped while it read: it changes nothing it was not let finish (review round 8).
    if (signal.aborted) return false;
    if (!upload || upload.ownerId !== this.ownerId) return false;
    this.publish(upload);
    if (upload.status === 'ready') return false;
    /**
     * An explicit retry retires the reason it is retrying, on BOTH branches.
     *
     * A refusal from the second capacity check lands here with every byte
     * already on the server, so it takes the reconciliation branch below -
     * which used to return without clearing anything. The upload stayed
     * parked on a code the person had just fixed, and if that reconciliation
     * then hit a transient failure nothing automatic picked it up again.
     */
    if (upload.status === 'failed'
      && (upload.lastErrorCode !== undefined || upload.lastErrorMessage !== undefined)) {
      upload = { ...upload, lastErrorCode: undefined, lastErrorMessage: undefined };
      await this.save(upload);
    }
    if (upload.offset === upload.totalBytes && upload.status === 'failed') {
      // The same reconciliation the automatic resume does, including the
      // walkthrough that the server has already turned into tickets: a person
      // pressing retry on one of those used to get a 409 and a row that
      // stayed failed however many times they pressed it.
      await this.resumeAfterMaster(upload, signal);
      return false;
    }
    if (upload.status === 'failed' || upload.status === 'pending') {
      upload = {
        ...upload,
        status: 'pending',
        updatedAt: this.nowIso(),
        lastErrorCode: undefined,
        lastErrorMessage: undefined,
      };
    }
    await this.save(upload);
    return true;
  }

  /** A caller of a retry asked while a run holds the slot waits here (see `queuedRetries`). */
  private waitForAskedRetry(recordingId: string): Promise<void> {
    let waiter = this.queuedRetries.get(recordingId);
    if (!waiter) {
      waiter = retryWaiter();
      this.queuedRetries.set(recordingId, waiter);
    }
    return waiter.done;
  }

  /** Makes an ask kept for when the slot was free, and lets its callers go when its transition is saved. */
  private makeAskedRetry(recordingId: string): void {
    const waiter = this.queuedRetries.get(recordingId);
    this.queuedRetries.delete(recordingId);
    if (!waiter) {
      void this.retry(recordingId).then(ignore, ignore);
      return;
    }
    const underWay = this.retriesUnderWay.get(recordingId) ?? new Set<RetryWaiter>();
    underWay.add(waiter);
    this.retriesUnderWay.set(recordingId, underWay);
    const settled = () => {
      underWay.delete(waiter);
      if (underWay.size === 0 && this.retriesUnderWay.get(recordingId) === underWay) this.retriesUnderWay.delete(recordingId);
      waiter.release();
    };
    void this.retry(recordingId).then(settled, settled);
  }

  /** Lets the callers of one recording's asks go, queued or under way, or every recording's. */
  private letRetryWaitersGo(recordingId?: string): void {
    const ids = recordingId === undefined
      ? [...new Set([...this.queuedRetries.keys(), ...this.retriesUnderWay.keys()])]
      : [recordingId];
    for (const id of ids) {
      this.queuedRetries.get(id)?.release();
      this.queuedRetries.delete(id);
      this.retriesUnderWay.get(id)?.forEach((waiter) => waiter.release());
      this.retriesUnderWay.delete(id);
    }
  }

  retryAll(): void {
    for (const upload of this.snapshot.uploads) {
      if (upload.status === 'pending'
        || (upload.status === 'failed' && !isParked(upload))) {
        this.retry(upload.recordingId);
      }
    }
  }
}
